import { datadogRum } from '@datadog/browser-rum';
import axios from 'axios';
import { StatusCodes } from 'http-status-codes';
import { maintenance_reason } from './constants';

const baseURL = `${process.env.REACT_APP_BACKEND_URL}`;

const isAuthEndpoint = (url = '') =>
  [
    'health/login/',
    'health/preLogin/',
    'health/resendOTP/',
    'health/token/refresh/',
  ].some((endpoint) => url.includes(endpoint));

const axiosInstance = axios.create({
  baseURL,
  timeout: 0,
  headers: {
    'Content-Type': 'application/json',
    accept: 'application/json',
  },
});

const refreshClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

Date.prototype.toJSON = function () {
  const hoursDiff = this.getHours() - this.getTimezoneOffset() / 60;
  this.setHours(hoursDiff);
  return this.toISOString();
};

const clearSession = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('role');
  localStorage.removeItem('rightholder');
  localStorage.removeItem('rightholder_id');
  delete axiosInstance.defaults.headers['Authorization'];
  datadogRum.clearUser();
};

const redirectToLogin = () => {
  clearSession();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

let refreshPromise = null;

const postRefresh = () =>
  refreshClient
    .post('/health/token/refresh/', {
      refresh: localStorage.getItem('refresh_token'),
    })
    .then(({ data }) => {
      localStorage.setItem('access_token', data.access);
      if (data.refresh) {
        localStorage.setItem('refresh_token', data.refresh);
      }
      axiosInstance.defaults.headers['Authorization'] =
        `JWT ${data.access}`;
      return data.access;
    });

export const refreshAccessToken = (usedToken) => {
  if (refreshPromise) return refreshPromise;

  if (!localStorage.getItem('refresh_token')) {
    redirectToLogin();
    return Promise.reject(new Error('refresh_token missing'));
  }

  const run = () => {
    const current = localStorage.getItem('access_token');
    if (usedToken && current && current !== usedToken)
      return Promise.resolve(current);
    return postRefresh();
  };

  // navigator.locks for multiple tabs in navigator
  refreshPromise = (
    navigator.locks
      ? navigator.locks.request('etiq-token-refresh', run)
      : run()
  )
    .catch((err) => {
      if (err.response) redirectToLogin();
      throw err;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

axiosInstance.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem('access_token');
  if (accessToken) {
    config.headers['Authorization'] = `JWT ${accessToken}`;
    config.__accessToken = accessToken;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => {
    if (String(response.status)[0] === '2')
      // type 2XX
      return Promise.resolve(response);
    else return Promise.reject(response);
  },
  (error) => {
    const { response, config: originalRequest } = error;
    if (!response) return Promise.reject(error);

    if (
      response.status === StatusCodes.SERVICE_UNAVAILABLE &&
      response.data?.metadata?.reason === maintenance_reason
    ) {
      return Promise.reject(error);
    }
    if (
      response.status === StatusCodes.UNAUTHORIZED &&
      originalRequest &&
      !originalRequest._retry &&
      !isAuthEndpoint(originalRequest.url)
    ) {
      originalRequest._retry = true;
      return refreshAccessToken(originalRequest.__accessToken)
        .then((token) => {
          originalRequest.headers['Authorization'] = `JWT ${token}`;
          return axiosInstance(originalRequest);
        })
        .catch(() => Promise.reject(response?.data));
    }

    return Promise.reject(response?.data);
  },
);

export const isAuthenticated = () => {
  const token = localStorage.getItem('access_token');
  return !!token;
};

export const isPro = () => {
  const role = localStorage.getItem('role');
  return isAuthenticated() && role === 'p';
};

export const rightholder = () => {
  let rightholder = localStorage.getItem('rightholder');
  if (rightholder === 'null') {
    rightholder = null;
  } else {
    rightholder = JSON.parse(rightholder);
  }
  return isAuthenticated() && rightholder;
};

export const logoutUser = () => {
  clearSession();
  window.location.href = '/';
};

export default axiosInstance;
